import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

import { useCurrentPageType, useResponsive } from "shared/hooks";
import { Edition, useEdition } from "shared/lib/edition";
import { PageType } from "shared/lib/page-type";
import { theme } from "shared/lib/styles";

import { appAtom } from "scmp-app/lib/app/atoms";

import { EditionInvitation } from ".";

export const useEditionInvitation = (onClose?: () => void) => {
  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { isInitialHasSelected: isInitialHasSelectedEdition, update: updateEdition } = useEdition({
    initializeWithValue: true,
  });

  const { asPath } = useRouter();
  const isDesktop = useResponsive(theme.breakpoints.up("tablet"), false);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const pageType = useCurrentPageType();

  const editionFromRoute = useMemo(() => {
    switch (asPath) {
      case "/asia":
      case "/asia/opinion":
        return Edition.Asia;
      case "/hk":
      case "/hk/opinion":
        return Edition.HongKong;
      default:
        return Edition.International;
    }
  }, [asPath]);

  const suggestEdition = useMemo(() => {
    switch (navigatorInfo?.country) {
      case "AU":
      case "CN":
      case "ID":
      case "IN":
      case "JP":
      case "KR":
      case "MO":
      case "MY":
      case "NZ":
      case "PH":
      case "SG":
      case "TW":
      case "VN":
        return Edition.Asia;
      case "HK":
        return Edition.HongKong;
      default:
        return Edition.International;
    }
  }, [navigatorInfo?.country]);

  const handleEditionSet = useCallback(
    (edition?: Edition) => {
      updateEdition(edition ?? suggestEdition);
    },
    [suggestEdition, updateEdition],
  );

  const [isClose, setIsClose] = useState(false);
  const handleClose = () => {
    onClose?.();
    setIsClose(true);
  };

  const result = { component: undefined, isLoading: !navigatorInfoLoaded };

  // Show the edition invitation has below conditions
  // 1. desktop or mobile
  // 2. Current page is home page
  // 3. Geolocation is ready
  // 4. Edition in route is not same as suggested
  // 5. Edition cookie has no values
  // 6. Close state has value
  if (!isDesktop && !isMobile) return result;
  else if (pageType !== PageType.Homepage) return result;
  else if (!navigatorInfoLoaded) return result;
  else if (editionFromRoute === suggestEdition) return result;
  else if (isInitialHasSelectedEdition) return result;
  else if (isClose) return result;

  return {
    ...result,
    component: (
      <EditionInvitation
        editionFromRoute={editionFromRoute}
        onClose={handleClose}
        onEditionSet={handleEditionSet}
        suggestEdition={suggestEdition}
      />
    ),
  };
};
